/**
 * The allowlist / deny-list manifest for Settings → Backup & Transfer (B12).
 *
 * ONE rule governs this file: **a setting travels only if it is named here.**
 * Nothing is exported by "take the whole store" — every field is listed, and
 * an object-valued field must either prune to named `subFields` or carry a
 * written `deep` reason. Both are audited by `settingsBundleManifest.test.ts`.
 *
 * Deny-listed data lives in TWO storage layers and both are listed below:
 *   1. renderer `localStorage` zustand `persist` stores → `DENIED_STORE_KEYS`
 *   2. main-process JSON files under `{userData}`      → `DENIED_MAIN_PROCESS_SOURCES`
 *
 * The exporter reads exactly ONE main-process source (`metainfo:list-all`);
 * `ALLOWED_ELECTRON_API_NAMESPACES` is enforced by a source-text test so a
 * future edit cannot quietly reach for a second one.
 */
import type { SettingsBundleGroup } from './settingsBundleGroups';

export { SETTINGS_BUNDLE_GROUPS } from './settingsBundleGroups';
export type {
  ExportedField,
  ExportedFieldType,
  ExportedStoreSlice,
  SettingsBundleGroup,
} from './settingsBundleGroups';

import { SETTINGS_BUNDLE_GROUPS } from './settingsBundleGroups';

export type SettingsBundleGroupId =
  | 'ui'
  | 'editor'
  | 'ai'
  | 'spatial'
  | 'toolbar'
  | 'keyboardShortcuts'
  | 'beta'
  | 'azure-storage'
  | 'snippets';

/**
 * Renderer `localStorage` stores whose contents must NEVER reach an export
 * file.
 *
 *  - `sql-studio-auth` / `sql-studio-license` — credentials and entitlement.
 *  - `sql-studio-session` — full workspace sessions, i.e. query text.
 *  - `sql-studio-query-history` — query text by definition.
 *  - `sql-studio-script-browser` — on-disk script paths.
 *  - `sql-studio-tab-history` — NOT obvious, and the reason this list is
 *    longer than the plan's: `TabHistoryEntry` carries BOTH `query` (the tab's
 *    SQL text) and `filePath` / `baselineFilePath` / `compareFilePath`. It is a
 *    second holder of the two things the plan names, under a name that reads
 *    like harmless UI state.
 *  - `sql-studio-recent-schema-compare` — connection ids bound to database and
 *    object names. Not a credential, but content, and meaningless on another
 *    machine. Deny-listing it is the safe direction of error.
 *  - `sql-studio-recent-data-compare` — B4's Data Compare twin of the above,
 *    landed on a branch that predates this manifest. Same shape (connection
 *    ids bound to database/schema/table names and their column mappings) and
 *    it additionally snapshots the source and target SERVER HOSTNAMES, so it
 *    is denied for the same reason, only more so.
 *  - `jam-sql-package-pins` — pinned Oracle package names per connection.
 *    Exactly the class the `pinnedobjects` key pattern exists for, and it was
 *    invisible to the first version of the drift scan because it uses the
 *    newer `jam-sql-` prefix.
 *  - `jam-sql-storage-preview-drafts` — unsaved edits of Azure Storage files
 *    (the text itself plus container / share paths), keyed by this machine's
 *    tab ids. User content bound to connections, like the session store.
 */
export const DENIED_STORE_KEYS = [
  'sql-studio-auth',
  'sql-studio-license',
  'sql-studio-session',
  'sql-studio-query-history',
  'sql-studio-script-browser',
  'sql-studio-tab-history',
  'sql-studio-recent-schema-compare',
  'sql-studio-recent-data-compare',
  // The `jam-sql-` prefix is the newer convention; the drift test scans for
  // BOTH prefixes, because scanning only `sql-studio-` is exactly how these
  // stores went unclassified in the first place.
  'jam-sql-package-pins',
  'jam-sql-storage-preview-drafts',
] as const;

/**
 * Main-process `{userData}` files that must NEVER be read by, or appear
 * inside, an export bundle. `connections.json` holds safeStorage-wrapped
 * passwords and inline SSH secrets (its portable form is task B13, not this
 * one); `auth-tokens.dat` is the OIDC token set; the licensing files bind a
 * device; `mcp-server-config.json` carries an MCP token.
 */
export const DENIED_MAIN_PROCESS_SOURCES = [
  'connections.json',
  'mcp-server-config.json',
  'auth-tokens.dat',
  'app-settings.json',
  'schema-annotations.json',
] as const;

/**
 * Substrings that must never appear in an exported object KEY, matched after
 * lowercasing and stripping non-alphanumerics (so `api_key`, `API-KEY` and
 * `apiKey` all match). Deliberately paranoid: this is the tripwire that fires
 * when someone later adds a field to an already-allowlisted object.
 *
 * The last four entries are not secrets but are excluded by decision D7 or by
 * being machine/connection-bound; listing them here makes the exclusion
 * enforced rather than merely intended.
 */
export const DENIED_KEY_PATTERNS = [
  'password',
  'passwd',
  'passphrase',
  'secret',
  'token',
  'apikey',
  'accesskey',
  'privatekey',
  'credential',
  'connectionstring',
  'licensekey',
  'pinnedobjects',
  // D7 / machine-bound exclusions.
  'analyticsenabled',
  'crashreportmode',
  'customstyleurl',
  'persistentworkspaces',
] as const;

/**
 * Persisted stores that are neither exported nor secret — recorded so the
 * inventory-drift test can prove every store in the repo has been classified.
 * A store here is a deliberate "not worth transferring", not an oversight.
 */
export const CLASSIFIED_NOT_EXPORTED: ReadonlyArray<{
  store: string;
  reason: string;
}> = [
  {
    store: 'jam-sql-review-prompt',
    reason:
      'Review-prompt counters and dismissal state. A nudge flag by D7, and ' +
      'transferring "already asked this user" would re-arm or suppress the ' +
      'prompt based on someone else\'s usage.',
  },
  {
    store: 'jam-sql-data-import-columns-coachmark',
    reason: 'A single first-visit coachmark dismissal flag. Nudge flag, dropped per D7.',
  },
  {
    store: 'jam-sql-import-discovery-card',
    reason:
      'Dismissal flag for the start-page "Found N connections in …" card. A ' +
      'nudge flag by D7, and it answers a question about THIS machine — the ' +
      'card only ever appears where another tool\'s connections were detected.',
  },
  {
    store: 'jam-sql-azure-picker',
    reason:
      'Preferred Azure identity (az-cli vs Entra). Depends on which Azure ' +
      'tooling is installed and signed in on THIS machine, so carrying the ' +
      'preference across would point at something that may not exist.',
  },
  {
    store: 'sql-studio-schema-compare-option-defaults',
    reason:
      'IMPLICIT last-used Schema Compare options per engine (F07) — object-type ' +
      'include flags and ignored-property keys, no connection/database/object ' +
      'names, so unlike sql-studio-recent-schema-compare it is not denied for ' +
      'content. It is withheld because it records what someone LAST DID rather ' +
      'than what they chose to keep: importing a colleague\'s bundle would ' +
      'silently narrow what the next comparison scans, with no UI saying so. ' +
      'Revisit when the "save as my defaults" affordance lands and the value ' +
      'becomes an explicit, user-declared preference — then it belongs in an ' +
      'export group, not here.',
  },
  {
    store: 'jam-sql-storage-preview',
    reason:
      'Azure Storage preview pane layout: open or closed, width, stacked height ' +
      'and the last viewing mode per format. Layout state like the other panel ' +
      'sizes, sized for THIS machine\'s window, so not worth transferring.',
  },
];

/** The only main-process read the exporter performs. */
export const ALLOWED_MAIN_PROCESS_SOURCES = ['metainfo:list-all'] as const;

/**
 * The only `window.electronAPI` namespaces the settings-transfer slice may
 * touch. Enforced by `settingsTransferSources.test.ts` over the folder's
 * source text — a new namespace fails that test until it is justified here.
 */
export const ALLOWED_ELECTRON_API_NAMESPACES = [
  'metainfo',
  'settingsTransfer',
  'testMode',
] as const;

export type DeniedStoreKey = (typeof DENIED_STORE_KEYS)[number];

/** Normalized form used for key matching: lowercase, alphanumerics only. */
export function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const DENIED_STORE_SET = new Set<string>(DENIED_STORE_KEYS);
const DENIED_SOURCE_SET = new Set<string>(DENIED_MAIN_PROCESS_SOURCES);

export function isDeniedStoreKey(key: string): boolean {
  return DENIED_STORE_SET.has(key) || DENIED_SOURCE_SET.has(key);
}

export function findDeniedKeyPattern(key: string): string | undefined {
  const normalized = normalizeKey(key);
  return DENIED_KEY_PATTERNS.find((pattern) => normalized.includes(pattern));
}

/** Every store key any manifest group is allowed to write into a bundle. */
export function allowedStoreKeys(
  groups: readonly SettingsBundleGroup[] = SETTINGS_BUNDLE_GROUPS,
): Set<string> {
  const out = new Set<string>();
  for (const group of groups) {
    for (const slice of group.slices) out.add(slice.store);
  }
  return out;
}

export function allowedGroupIds(
  groups: readonly SettingsBundleGroup[] = SETTINGS_BUNDLE_GROUPS,
): Set<string> {
  return new Set(groups.map((g) => g.id));
}

export function groupById(
  id: string,
  groups: readonly SettingsBundleGroup[] = SETTINGS_BUNDLE_GROUPS,
): SettingsBundleGroup | undefined {
  return groups.find((g) => g.id === id);
}
